import React, { useState } from 'react';
import { ShoppingCartIcon, HeartIcon, XIcon } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Notification } from './Notification';
type Size = 'S' | 'M' | 'L' | 'XL' | '2XL';
type Color = 'Black' | 'White' | 'Gray';
interface Product {
  id: number;
  name: string;
  type: 'T-Shirt' | 'Hoodie';
  price: number;
  colors: Color[];
  sizes: Size[];
  image: string;
  material: string;
}
const products: Product[] = [{
  id: 1,
  name: 'Chicago Kush Classic Tee',
  type: 'T-Shirt',
  price: 35.0,
  colors: ['Black', 'White', 'Gray'],
  sizes: ['S', 'M', 'L', 'XL', '2XL'],
  image: 'placeholder',
  material: '100% Premium Cotton'
}, {
  id: 2,
  name: 'Chicago Kush Premium Hoodie',
  type: 'Hoodie',
  price: 65.0,
  colors: ['Black', 'Gray'],
  sizes: ['S', 'M', 'L', 'XL', '2XL'],
  image: 'placeholder',
  material: '80% Cotton, 20% Polyester'
}, {
  id: 3,
  name: 'Chicago Kush Logo Tee',
  type: 'T-Shirt',
  price: 40.0,
  colors: ['Black', 'White'],
  sizes: ['S', 'M', 'L', 'XL', '2XL'],
  image: 'placeholder',
  material: '100% Premium Cotton'
}, {
  id: 4,
  name: 'Chicago Kush Zip Hoodie',
  type: 'Hoodie',
  price: 75.0,
  colors: ['Black', 'Gray'],
  sizes: ['S', 'M', 'L', 'XL', '2XL'],
  image: 'placeholder',
  material: '80% Cotton, 20% Polyester'
}];
const ColorSwatch: React.FC<{
  color: Color;
  selected: boolean;
  onClick: () => void;
}> = ({
  color,
  selected,
  onClick
}) => {
  const bgColor = color === 'Black' ? 'bg-black' : color === 'White' ? 'bg-white' : 'bg-gray-500';
  return <button onClick={onClick} className={`w-6 h-6 rounded-full ${bgColor} border-2 ${selected ? 'border-red-500' : 'border-transparent'} transition duration-300`} data-id="element-235" />;
};
const ProductCard: React.FC<{
  product: Product;
}> = ({
  product
}) => {
  const [selectedColor, setSelectedColor] = useState<Color>(product.colors[0]);
  const [selectedSize, setSelectedSize] = useState<Size>('M');
  const [showNotification, setShowNotification] = useState(false);
  const {
    dispatch
  } = useCart();
  const handleAddToCart = () => {
    dispatch({
      type: 'ADD_ITEM',
      payload: {
        id: product.id,
        name: product.name,
        price: product.price,
        quantity: 1,
        image: product.image,
        size: selectedSize,
        color: selectedColor
      }
    });
    setShowNotification(true);
  };
  return <div className="bg-glass border border-red-600/20 rounded-xl overflow-hidden hover:bg-glass-hover hover:border-red-500/50 transition duration-300 group" data-id="element-236">
      <div className="h-80 bg-black/50 flex items-center justify-center" data-id="element-237">
        <h3 className="font-display italic text-3xl text-red-500" data-id="element-238">
          Coming Soon
        </h3>
      </div>
      <div className="p-6" data-id="element-239">
        <div className="flex justify-between items-start mb-2" data-id="element-240">
          <div data-id="element-241">
            <h3 className="font-display italic text-xl font-bold text-white" data-id="element-242">
              {product.name}
            </h3>
            <p className="font-body italic text-gray-400" data-id="element-243">{product.type}</p>
          </div>
          <span className="font-display text-lg text-white" data-id="element-244">
            ${product.price}
          </span>
        </div>
        <div className="space-y-4 mt-4" data-id="element-245">
          <div data-id="element-246">
            <p className="text-sm text-gray-400 mb-2" data-id="element-247">Color</p>
            <div className="flex space-x-2" data-id="element-248">
              {product.colors.map(color => <ColorSwatch key={color} color={color} selected={selectedColor === color} onClick={() => setSelectedColor(color)} data-id="element-249" />)}
            </div>
          </div>
          <div data-id="element-250">
            <p className="text-sm text-gray-400 mb-2" data-id="element-251">Size</p>
            <div className="flex flex-wrap gap-2" data-id="element-252">
              {product.sizes.map(size => <button key={size} onClick={() => setSelectedSize(size)} className={`px-3 py-1 rounded-full text-sm transition duration-300 ${selectedSize === size ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`} data-id="element-253">
                  {size}
                </button>)}
            </div>
          </div>
          <p className="text-xs text-gray-400" data-id="element-254">{product.material}</p>
          <div className="flex gap-2" data-id="element-255">
            <button onClick={handleAddToCart} className="font-display italic flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-full transition duration-300 flex items-center justify-center" data-id="element-256">
              <ShoppingCartIcon size={18} className="mr-2" data-id="element-257" />
              Add to Cart
            </button>
            <button className="font-display italic bg-gray-800 hover:bg-gray-700 text-white p-2 rounded-full transition duration-300" data-id="element-258">
              <HeartIcon size={18} data-id="element-259" />
            </button>
          </div>
        </div>
      </div>
    </div>;
};
const ProductModal: React.FC<ProductModalProps> = ({
  product,
  onClose
}) => {
  return <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-id="element-260">
      <div className="bg-glass border border-red-600/20 rounded-xl max-w-2xl w-full p-6 relative" data-id="element-261">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white" data-id="element-262">
          <XIcon size={24} data-id="element-263" />
        </button>
        <div className="text-center py-12" data-id="element-264">
          <h3 className="font-display italic text-2xl font-bold text-red-500 mb-4" data-id="element-265">
            Retail Locations Coming Soon
          </h3>
          <p className="text-gray-300" data-id="element-266">
            Stay tuned for our retail store openings.
          </p>
        </div>
      </div>
    </div>;
};
export const Store = () => {
  return <section id="store" className="py-20 bg-gradient-dark" data-id="element-267">
      <div className="container mx-auto px-4" data-id="element-268">
        <div className="text-center mb-16" data-id="element-269">
          <h2 className="font-display italic text-3xl sm:text-4xl font-bold mb-4" data-id="element-270">
            Official <span className="text-red-500" data-id="element-271">Merch</span>
          </h2>
          <p className="font-body italic text-gray-400 max-w-2xl mx-auto" data-id="element-272">
            Copywrite needed
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-273">
          {products.map(product => <ProductCard key={product.id} product={product} data-id="element-274" />)}
        </div>
      </div>
    </section>;
};